import * as React from "react";
import PropTypes from "prop-types";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ButtonGroup from "@mui/material/ButtonGroup";
import Button from "@mui/material/Button";

import "../../common/Common.css";
import InvoiceTable from "./InvoiceTable";
import api from "../../services/api";

// ---------------------------------------------------------
// Palette
// ---------------------------------------------------------
const palette = {
  indigo: "#4B49AC",
  indigoDark: "#37357D",
  ink: "#1F2937",
  slate: "#6B7280",
  line: "#E6E9ED",
  bg: "#FAFBFC",
  teal: "#12A594",
  tealSoft: "#DDF6F1",
  coral: "#FF6B57",
  coralSoft: "#FFEFEC",
  blue: "#3B82F6",
  blueSoft: "#EFF6FF",
  amber: "#D97706",
  amberSoft: "#FFF7E8",
  rose: "#B91C1C",
  roseSoft: "#FEE2E2",
};

// ---------------------------------------------------------
// Dynamic color palette
// Used for services, payment modes, payment statuses, and
// invoice statuses — anywhere the set of categories is driven
// by the API rather than hardcoded. Colors are assigned by
// index so they stay consistent between a chart and its legend.
// ---------------------------------------------------------
const COLOR_PALETTE = [
  { solid: palette.indigo, soft: "#F0EDFF" },
  { solid: palette.teal, soft: palette.tealSoft },
  { solid: palette.coral, soft: palette.coralSoft },
  { solid: palette.blue, soft: palette.blueSoft },
  { solid: palette.amber, soft: palette.amberSoft },
];

const getPaletteColor = (index) =>
  COLOR_PALETTE[index % COLOR_PALETTE.length];

// Aging buckets go from "fine" to "urgent" — fixed severity colors
// rather than the cycling palette, so 90+ always reads as most severe.
const AGING_BUCKET_COLORS = {
  "0-30": palette.teal,
  "31-60": palette.amber,
  "61-90": palette.coral,
  "90+": palette.rose,
};

const EMPTY_DASHBOARD_DATA = {
  total_revenue: 0,
  pending_payment: 0,
  service_revenue: [],
  payment_mode_breakdown: [],
  payment_status_breakdown: [],
  invoice_status_breakdown: [],
  outstanding_aging: [],
  revenue_trend_monthly: [],
  revenue_trend_yearly: [],
  avg_invoice_value_trend: [],
};

// ---------------------------------------------------------
// Tab panel
// ---------------------------------------------------------
function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      className="branch_tab"
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

// ---------------------------------------------------------
// Accessibility
// ---------------------------------------------------------
function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
    className: "branch_tab",
  };
}

// ---------------------------------------------------------
// Format currency
// ---------------------------------------------------------
const formatCurrency = (value) => {
  return `₹${Number(value || 0).toLocaleString("en-IN")}`;
};

const formatLabel = (value) => {
  if (!value) return "-";
  return String(value)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// ---------------------------------------------------------
// Dashboard Card
// ---------------------------------------------------------
function DashboardCard({ title, value, subtitle, icon, background }) {
  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        border: `1px solid ${palette.line}`,
        borderRadius: "12px",
        padding: "18px",
        minHeight: "120px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{ fontSize: "13px", fontWeight: 600, color: palette.slate }}
        >
          {title}
        </Typography>

        <Box
          sx={{
            width: "34px",
            height: "34px",
            borderRadius: "9px",
            backgroundColor: background,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "17px",
          }}
        >
          {icon}
        </Box>
      </Box>

      <Box>
        <Typography
          sx={{
            fontSize: "25px",
            fontWeight: 700,
            color: palette.ink,
            lineHeight: 1.2,
          }}
        >
          {value}
        </Typography>

        {subtitle && (
          <Typography
            sx={{
              marginTop: "5px",
              fontSize: "11.5px",
              color: palette.slate,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------
// Revenue Trend Chart (used for both Monthly and Yearly views)
// ---------------------------------------------------------
// `data` — array of { label, services: { [serviceName]: amount }, total }
// `services` — ordered list of service names (color + legend + draw order)
// ---------------------------------------------------------
function RevenueTrendChart({ data, services }) {
  const width = 900;
  const height = 330;

  const paddingLeft = 65;
  const paddingRight = 30;
  const paddingTop = 35;
  const paddingBottom = 55;

  const chartWidth = width - paddingLeft - paddingRight;
  const chartHeight = height - paddingTop - paddingBottom;

  if (!data?.length) {
    return (
      <Box
        sx={{
          padding: "40px",
          textAlign: "center",
          fontSize: "13px",
          color: palette.slate,
        }}
      >
        No revenue data for this period yet.
      </Box>
    );
  }

  const allValues = data.flatMap((item) => [
    ...services.map((name) => Number(item.services?.[name] || 0)),
    Number(item.total || 0),
  ]);

  const maxRaw = Math.max(...allValues, 10000);
  const maxValue = Math.ceil(maxRaw / 10000) * 10000;

  const ySteps = 5;

  const getX = (index) => {
    if (data.length === 1) {
      return paddingLeft + chartWidth / 2;
    }
    return paddingLeft + (index / (data.length - 1)) * chartWidth;
  };

  const getY = (value) => {
    return (
      paddingTop + chartHeight - (Number(value || 0) / maxValue) * chartHeight
    );
  };

  const totalPoints = data
    .map((item, index) => `${getX(index)},${getY(item.total)}`)
    .join(" ");

  const servicePointsByName = {};
  services.forEach((name) => {
    servicePointsByName[name] = data
      .map(
        (item, index) => `${getX(index)},${getY(item.services?.[name])}`
      )
      .join(" ");
  });

  return (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height="330"
        preserveAspectRatio="none"
      >
        {/* Y axis */}
        {Array.from({ length: ySteps + 1 }).map((_, index) => {
          const axisValue = (maxValue / ySteps) * index;
          const y = getY(axisValue);

          return (
            <React.Fragment key={index}>
              <line
                x1={paddingLeft}
                y1={y}
                x2={width - paddingRight}
                y2={y}
                stroke={palette.line}
                strokeWidth="1"
              />

              <text
                x={paddingLeft - 12}
                y={y + 4}
                textAnchor="end"
                fontSize="11"
                fill={palette.slate}
              >
                {axisValue >= 1000
                  ? `₹${axisValue / 1000}K`
                  : `₹${axisValue}`}
              </text>
            </React.Fragment>
          );
        })}

        {/* X axis */}
        {data.map((item, index) => (
          <text
            key={`${item.label}-${index}`}
            x={getX(index)}
            y={height - 20}
            textAnchor="middle"
            fontSize="11"
            fill={palette.slate}
          >
            {item.label}
          </text>
        ))}

        {/* Per-service lines */}
        {services.map((name, index) => (
          <polyline
            key={`line-${name}`}
            points={servicePointsByName[name]}
            fill="none"
            stroke={getPaletteColor(index).solid}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}

        {/* Total */}
        <polyline
          points={totalPoints}
          fill="none"
          stroke={palette.amber}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="7 5"
        />

        {/* Per-service points */}
        {services.map((name, sIndex) =>
          data.map((item, index) => (
            <g key={`${name}-${item.label}-${index}`}>
              <circle
                cx={getX(index)}
                cy={getY(item.services?.[name])}
                r="5"
                fill="#FFFFFF"
                stroke={getPaletteColor(sIndex).solid}
                strokeWidth="3"
              />
              <title>
                {`${item.label} - ${name}: ${formatCurrency(
                  item.services?.[name]
                )}`}
              </title>
            </g>
          ))
        )}

        {/* Total points */}
        {data.map((item, index) => (
          <g key={`total-${item.label}-${index}`}>
            <circle
              cx={getX(index)}
              cy={getY(item.total)}
              r="5.5"
              fill={palette.amber}
              stroke="#FFFFFF"
              strokeWidth="2"
            />
            <title>{`${item.label} - Total: ${formatCurrency(item.total)}`}</title>
          </g>
        ))}
      </svg>
    </Box>
  );
}

// ---------------------------------------------------------
// Simple single-series line chart (used for Avg Invoice Value)
// ---------------------------------------------------------
function SimpleLineChart({ data, valueKey, color, valueFormatter }) {
  const width = 900;
  const height = 220;

  const paddingLeft = 65;
  const paddingRight = 30;
  const paddingTop = 25;
  const paddingBottom = 40;

  const chartWidth = width - paddingLeft - paddingRight;
  const chartHeight = height - paddingTop - paddingBottom;

  if (!data?.length) {
    return null;
  }

  const values = data.map((item) => Number(item[valueKey] || 0));
  const maxRaw = Math.max(...values, 100);
  const maxValue = Math.ceil(maxRaw / 100) * 100;

  const getX = (index) => {
    if (data.length === 1) return paddingLeft + chartWidth / 2;
    return paddingLeft + (index / (data.length - 1)) * chartWidth;
  };

  const getY = (value) =>
    paddingTop + chartHeight - (Number(value || 0) / maxValue) * chartHeight;

  const points = data
    .map((item, index) => `${getX(index)},${getY(item[valueKey])}`)
    .join(" ");

  const format = valueFormatter || formatCurrency;

  return (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height="220"
        preserveAspectRatio="none"
      >
        {Array.from({ length: 4 }).map((_, index) => {
          const axisValue = (maxValue / 3) * index;
          const y = getY(axisValue);

          return (
            <React.Fragment key={index}>
              <line
                x1={paddingLeft}
                y1={y}
                x2={width - paddingRight}
                y2={y}
                stroke={palette.line}
                strokeWidth="1"
              />
              <text
                x={paddingLeft - 12}
                y={y + 4}
                textAnchor="end"
                fontSize="11"
                fill={palette.slate}
              >
                {format(axisValue)}
              </text>
            </React.Fragment>
          );
        })}

        {data.map((item, index) => (
          <text
            key={`${item.label}-${index}`}
            x={getX(index)}
            y={height - 12}
            textAnchor="middle"
            fontSize="11"
            fill={palette.slate}
          >
            {item.label}
          </text>
        ))}

        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {data.map((item, index) => (
          <g key={`point-${item.label}-${index}`}>
            <circle
              cx={getX(index)}
              cy={getY(item[valueKey])}
              r="5"
              fill="#FFFFFF"
              stroke={color}
              strokeWidth="3"
            />
            <title>{`${item.label}: ${format(item[valueKey])}`}</title>
          </g>
        ))}
      </svg>
    </Box>
  );
}

// ---------------------------------------------------------
// Donut chart + legend — used for payment mode / status / invoice status
// ---------------------------------------------------------
function DonutBreakdown({ data, size = 150, thickness = 20 }) {
  const total = data.reduce((sum, d) => sum + Number(d.value || 0), 0);
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;

  let cumulative = 0;

  if (!total) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: size,
          fontSize: "12px",
          color: palette.slate,
        }}
      >
        No data yet
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "18px",
        flexWrap: "wrap",
      }}
    >
      <Box sx={{ position: "relative", flexShrink: 0 }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
            {data.map((d) => {
              const value = Number(d.value || 0);
              const fraction = total ? value / total : 0;
              const dash = fraction * circumference;
              const gap = circumference - dash;
              const offset = -cumulative * circumference;
              cumulative += fraction;

              return (
                <circle
                  key={d.label}
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke={d.color}
                  strokeWidth={thickness}
                  strokeDasharray={`${dash} ${gap}`}
                  strokeDashoffset={offset}
                />
              );
            })}
          </g>
        </svg>

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{ fontSize: "13px", fontWeight: 700, color: palette.ink }}
          >
            {formatCurrency(total)}
          </Typography>
          <Typography sx={{ fontSize: "10px", color: palette.slate }}>
            Total
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1, minWidth: 140 }}>
        {data.map((d) => {
          const pct = total ? ((Number(d.value || 0) / total) * 100).toFixed(0) : 0;

          return (
            <Box
              key={d.label}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
                <Box
                  sx={{
                    width: "9px",
                    height: "9px",
                    borderRadius: "50%",
                    backgroundColor: d.color,
                    flexShrink: 0,
                  }}
                />
                <Typography sx={{ fontSize: "12px", color: palette.ink }}>
                  {d.label}
                </Typography>
              </Box>

              <Typography
                sx={{ fontSize: "11.5px", fontWeight: 600, color: palette.slate, whiteSpace: "nowrap" }}
              >
                {formatCurrency(d.value)} ({pct}%)
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------
// Horizontal bar list — used for Outstanding Aging
// ---------------------------------------------------------
function AgingBarList({ data }) {
  const maxAmount = Math.max(...data.map((d) => Number(d.amount || 0)), 1);
  const totalAmount = data.reduce((sum, d) => sum + Number(d.amount || 0), 0);

  if (!totalAmount) {
    return (
      <Box
        sx={{
          padding: "20px",
          textAlign: "center",
          fontSize: "12px",
          color: palette.slate,
        }}
      >
        No outstanding invoices — nothing pending.
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {data.map((d) => {
        const color = AGING_BUCKET_COLORS[d.bucket] || palette.slate;
        const widthPct = (Number(d.amount || 0) / maxAmount) * 100;

        return (
          <Box key={d.bucket}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "12px",
                marginBottom: "5px",
              }}
            >
              <Typography sx={{ fontSize: "12px", fontWeight: 600, color: palette.ink }}>
                {d.bucket} days
              </Typography>

              <Typography sx={{ fontSize: "12px", fontWeight: 700, color }}>
                {formatCurrency(d.amount)}{" "}
                <Typography component="span" sx={{ fontSize: "11px", color: palette.slate, fontWeight: 500 }}>
                  ({d.count} invoice{d.count === 1 ? "" : "s"})
                </Typography>
              </Typography>
            </Box>

            <Box
              sx={{
                height: "9px",
                borderRadius: "5px",
                backgroundColor: palette.bg,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${widthPct}%`,
                  backgroundColor: color,
                  borderRadius: "5px",
                  transition: "width 0.3s ease",
                }}
              />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// ---------------------------------------------------------
// Panel wrapper — consistent card chrome for each breakdown section
// ---------------------------------------------------------
function Panel({ title, subtitle, children, action }) {
  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        border: `1px solid ${palette.line}`,
        borderRadius: "12px",
        padding: "18px 20px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: "8px",
          marginBottom: "16px",
        }}
      >
        <Box>
          <Typography sx={{ fontSize: "14.5px", fontWeight: 700, color: palette.ink }}>
            {title}
          </Typography>

          {subtitle && (
            <Typography sx={{ fontSize: "11.5px", color: palette.slate, marginTop: "2px" }}>
              {subtitle}
            </Typography>
          )}
        </Box>

        {action}
      </Box>

      {children}
    </Box>
  );
}

// ---------------------------------------------------------
// Invoice Dashboard
// ---------------------------------------------------------
function InvoiceDashboard({ data, loading }) {
  const [trendMode, setTrendMode] = React.useState("monthly");

  const serviceRevenue = data?.service_revenue || [];
  const serviceNames = serviceRevenue.map((s) => s.service_name);

  const trendData =
    trendMode === "monthly"
      ? data?.revenue_trend_monthly || []
      : data?.revenue_trend_yearly || [];

  const paymentModeData = (data?.payment_mode_breakdown || []).map((d, i) => ({
    label: formatLabel(d.mode),
    value: d.amount,
    count: d.count,
    color: getPaletteColor(i).solid,
  }));

  const paymentStatusData = (data?.payment_status_breakdown || []).map((d, i) => ({
    label: formatLabel(d.status),
    value: d.amount,
    count: d.count,
    color: getPaletteColor(i).solid,
  }));

  const invoiceStatusData = (data?.invoice_status_breakdown || []).map((d, i) => ({
    label: formatLabel(d.status),
    value: d.count,
    color: getPaletteColor(i).solid,
  }));

  return (
    <Box sx={{ marginBottom: "25px" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          marginBottom: "15px",
        }}
      >
        <Box>
          <Typography sx={{ fontSize: "18px", fontWeight: 700, color: palette.ink }}>
            Invoice Dashboard
          </Typography>

          <Typography sx={{ fontSize: "12px", color: palette.slate, marginTop: "3px" }}>
            Revenue and billing overview
          </Typography>
        </Box>
      </Box>

      {/* Summary cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
          gap: "14px",
        }}
      >
        <DashboardCard
          title="Total Revenue"
          value={formatCurrency(data?.total_revenue)}
          subtitle="Paid invoices"
          icon="₹"
          background={palette.blueSoft}
        />

        <DashboardCard
          title="Pending Payment"
          value={formatCurrency(data?.pending_payment)}
          subtitle="Outstanding amount"
          icon="◷"
          background={palette.coralSoft}
        />

        {serviceRevenue.map((s, index) => (
          <DashboardCard
            key={s.service_id}
            title={`${s.service_name} Revenue`}
            value={formatCurrency(s.revenue)}
            subtitle={s.service_name}
            icon={s.service_name.slice(0, 2).toUpperCase()}
            background={getPaletteColor(index).soft}
          />
        ))}
      </Box>

      {/* Revenue trend, with Monthly/Yearly toggle */}
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          border: `1px solid ${palette.line}`,
          borderRadius: "12px",
          padding: "20px",
          marginTop: "20px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
            marginBottom: "15px",
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "16px", fontWeight: 700, color: palette.ink }}>
              Revenue Trend
            </Typography>

            <Typography sx={{ fontSize: "12px", color: palette.slate, marginTop: "3px" }}>
              {trendMode === "monthly"
                ? "Last 6 months, by service"
                : "Last 3 years, by service"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: "18px", flexWrap: "wrap" }}>
            {/* Legend */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
                <Box sx={{ width: "18px", height: "3px", borderRadius: "2px", backgroundColor: palette.amber }} />
                <Typography sx={{ fontSize: "12px", fontWeight: 700, color: palette.slate }}>
                  Total
                </Typography>
              </Box>

              {serviceNames.map((name, index) => (
                <Box key={name} sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
                  <Box
                    sx={{
                      width: "9px",
                      height: "9px",
                      borderRadius: "50%",
                      backgroundColor: getPaletteColor(index).solid,
                    }}
                  />
                  <Typography sx={{ fontSize: "12px", color: palette.slate }}>
                    {name}
                  </Typography>
                </Box>
              ))}
            </Box>

            <ButtonGroup size="small" sx={{ borderRadius: "8px" }}>
              <Button
                variant={trendMode === "monthly" ? "contained" : "outlined"}
                onClick={() => setTrendMode("monthly")}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "12px",
                  backgroundColor: trendMode === "monthly" ? palette.indigo : "transparent",
                  borderColor: palette.indigo,
                  color: trendMode === "monthly" ? "#fff" : palette.indigo,
                  "&:hover": {
                    backgroundColor: trendMode === "monthly" ? palette.indigoDark : palette.bg,
                    borderColor: palette.indigo,
                  },
                }}
              >
                Monthly
              </Button>

              <Button
                variant={trendMode === "yearly" ? "contained" : "outlined"}
                onClick={() => setTrendMode("yearly")}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "12px",
                  backgroundColor: trendMode === "yearly" ? palette.indigo : "transparent",
                  borderColor: palette.indigo,
                  color: trendMode === "yearly" ? "#fff" : palette.indigo,
                  "&:hover": {
                    backgroundColor: trendMode === "yearly" ? palette.indigoDark : palette.bg,
                    borderColor: palette.indigo,
                  },
                }}
              >
                Yearly
              </Button>
            </ButtonGroup>
          </Box>
        </Box>

        {!loading && (
          <RevenueTrendChart data={trendData} services={serviceNames} />
        )}
      </Box>

      {/* Breakdown panels: payment mode, payment status, invoice status, aging */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: "16px",
          marginTop: "20px",
        }}
      >
        <Panel title="Payment Mode" subtitle="Cash / UPI / other — paid invoices">
          <DonutBreakdown data={paymentModeData} />
        </Panel>

        <Panel title="Payment Status" subtitle="Paid vs pending, by amount">
          <DonutBreakdown data={paymentStatusData} />
        </Panel>

        <Panel title="Invoice Status" subtitle="Draft / finalized / cancelled — by count">
          <DonutBreakdown data={invoiceStatusData} />
        </Panel>

        <Panel title="Outstanding Aging" subtitle="Pending invoices by age (from invoice date)">
          <AgingBarList data={data?.outstanding_aging || []} />
        </Panel>
      </Box>

      {/* Average invoice value trend */}
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          border: `1px solid ${palette.line}`,
          borderRadius: "12px",
          padding: "20px",
          marginTop: "20px",
        }}
      >
        <Typography sx={{ fontSize: "16px", fontWeight: 700, color: palette.ink }}>
          Average Invoice Value
        </Typography>

        <Typography sx={{ fontSize: "12px", color: palette.slate, marginTop: "3px", marginBottom: "10px" }}>
          Last 6 months, paid invoices
        </Typography>

        {!loading && (
          <SimpleLineChart
            data={data?.avg_invoice_value_trend || []}
            valueKey="avg_value"
            color={palette.indigo}
          />
        )}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------
// Main Component
// ---------------------------------------------------------
export default function InvoiceManagement({ userDetail }) {
  const [value, setValue] = React.useState(0);
  const [branch, setBranch] = React.useState([]);
  const [invoiceDetails, setInvoiceDetails] = React.useState([]);

  const [dashboardData, setDashboardData] = React.useState(EMPTY_DASHBOARD_DATA);
  const [dashboardLoading, setDashboardLoading] = React.useState(true);

  // -------------------------------------------------------
  // Load branches
  // -------------------------------------------------------
  React.useEffect(() => {
    userDetails();
  }, []);

  const userDetails = async () => {
    try {
      const response = await api.get("app/user/current_user", {});
      setBranch(response?.data?.branches || []);
    } catch (error) {
      console.error("Failed to load user details:", error);
    }
  };

  // -------------------------------------------------------
  // Load invoices for selected branch
  // -------------------------------------------------------
  const getInvoiceDetails = async (branchId) => {
    if (!branchId) {
      setInvoiceDetails([]);
      return;
    }

    try {
      const response = await api.get(`app/invoice/?branch_id=${branchId}`);
      setInvoiceDetails(response?.data || []);
    } catch (error) {
      console.error("Failed to load invoices:", error);
      setInvoiceDetails([]);
    }
  };

  // -------------------------------------------------------
  // Load dashboard summary for selected branch
  // -------------------------------------------------------
  const getDashboardData = async (branchId) => {
    if (!branchId) {
      setDashboardData(EMPTY_DASHBOARD_DATA);
      setDashboardLoading(false);
      return;
    }

    setDashboardLoading(true);

    try {
      const response = await api.get(`app/invoice/dashboard/?branch_id=${branchId}`);
      setDashboardData(response?.data || EMPTY_DASHBOARD_DATA);
    } catch (error) {
      console.error("Failed to load invoice dashboard:", error);
      setDashboardData(EMPTY_DASHBOARD_DATA);
    } finally {
      setDashboardLoading(false);
    }
  };

  // -------------------------------------------------------
  // Load invoices + dashboard when branch changes
  // -------------------------------------------------------
  React.useEffect(() => {
    const selectedBranch = branch?.[value];

    if (selectedBranch?.id) {
      getInvoiceDetails(selectedBranch.id);
      getDashboardData(selectedBranch.id);
    }
  }, [value, branch]);

  // -------------------------------------------------------
  // Tab change
  // -------------------------------------------------------
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  // -------------------------------------------------------
  // Render
  // -------------------------------------------------------
  return (
    <Box sx={{ width: "100%" }}>
      {/* ================================================= */}
      {/* Branch Tabs */}
      {/* ================================================= */}

      <Box
        sx={{
          borderBottom: `1px solid ${palette.line}`,
          marginBottom: "20px",
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="branch tabs"
          TabIndicatorProps={{
            sx: {
              backgroundColor: palette.indigo,
              height: "3px",
              borderRadius: "3px 3px 0 0",
            },
          }}
          sx={{
            minHeight: "42px",
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "13.5px",
              color: palette.slate,
              minHeight: "42px",
              padding: "8px 18px",
              "&:hover": { color: palette.indigoDark },
            },
            "& .Mui-selected": {
              color: `${palette.indigo} !important`,
            },
          }}
        >
          {branch?.map((b, index) => (
            <Tab
              key={b.id}
              label={b.name}
              {...a11yProps(index)}
              className="branch_name"
            />
          ))}
        </Tabs>
      </Box>

      {/* ================================================= */}
      {/* Branch Content */}
      {/* ================================================= */}

      {branch?.map((b, index) => (
        <CustomTabPanel key={b.id} value={value} index={index}>
          {/* Dashboard */}
          <InvoiceDashboard data={dashboardData} loading={dashboardLoading} />

          {/* Invoice Table */}
          <InvoiceTable
            rows={invoiceDetails}
            branchId={b.id}
            invoiceDetails={invoiceDetails}
            getInvoiceDetails={() => getInvoiceDetails(b.id)}
            userDetail={userDetail}
          />
        </CustomTabPanel>
      ))}
    </Box>
  );
}
